import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { theme } from '../styles/theme';
import Button from './ui/Button';

interface Webhook {
  id: number;
  webhookId: string;
  orgId: string;
  formName: string;
  description: string | null;
  webhookUrl: string;
  createdAt: string;
  updatedAt: string;
}

interface WebhookManagerProps {
  orgId: string;
}

const WebhookManager: React.FC<WebhookManagerProps> = ({ orgId }) => {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Load webhooks on mount
  useEffect(() => {
    loadWebhooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.webhooks.fetchByOrgId(orgId);
      if (response.success) {
        setWebhooks(response.webhooks);
      } else {
        setError('Failed to load webhooks');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebhook = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formName.trim()) {
      setError('Form name is required');
      return;
    }

    try {
      setCreating(true);
      setError('');
      const response = await api.webhooks.create(formName.trim(), description.trim() || undefined);

      if (response.success) {
        await loadWebhooks();
        setFormName('');
        setDescription('');
        setIsCreating(false);
      } else {
        setError('Failed to create webhook');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create webhook');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteWebhook = async (webhookId: string) => {
    if (!window.confirm('Are you sure you want to delete this webhook? This action cannot be undone.')) {
      return;
    }

    try {
      setError('');
      const response = await api.webhooks.delete(orgId, webhookId);

      if (response.success) {
        await loadWebhooks();
      } else {
        setError('Failed to delete webhook');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete webhook');
    }
  };

  const copyWebhookUrl = (webhookUrl: string, webhookId: string) => {
    navigator.clipboard.writeText(webhookUrl);
    setCopiedId(webhookId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div style={{
      padding: '32px',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
      }}>
        <div>
          <h1 style={{
            margin: '0 0 8px 0',
            fontSize: theme.typography.sizes['3xl'],
            fontWeight: theme.typography.weights.bold,
            color: theme.colors.text.primary
          }}>
            Typeform Webhooks
          </h1>
          <p style={{
            margin: 0,
            fontSize: theme.typography.sizes.base,
            color: theme.colors.text.secondary
          }}>
            Create and manage webhook URLs for your Typeform integrations
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          onClick={() => setIsCreating(!isCreating)}
        >
          {isCreating ? 'Cancel' : '+ Create Webhook'}
        </Button>
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FCA5A5',
          borderRadius: '8px',
          color: '#DC2626',
          marginBottom: '24px',
          fontSize: theme.typography.sizes.sm
        }}>
          {error}
        </div>
      )}

      {/* Create webhook form */}
      {isCreating && (
        <div style={{
          backgroundColor: 'white',
          border: `2px solid ${theme.colors.border}`,
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h2 style={{
            margin: '0 0 16px 0',
            fontSize: theme.typography.sizes.xl,
            fontWeight: theme.typography.weights.semibold,
            color: theme.colors.text.primary
          }}>
            Create New Webhook
          </h2>
          <form onSubmit={handleCreateWebhook}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: theme.typography.sizes.sm,
                fontWeight: theme.typography.weights.medium,
                color: theme.colors.text.primary
              }}>
                Form Name *
              </label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., Sales Call Booking Form"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '8px',
                  fontSize: theme.typography.sizes.base,
                  fontFamily: theme.typography.fontFamily
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: theme.typography.sizes.sm,
                fontWeight: theme.typography.weights.medium,
                color: theme.colors.text.primary
              }}>
                Description (Optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add notes about this webhook..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '8px',
                  fontSize: theme.typography.sizes.base,
                  fontFamily: theme.typography.fontFamily,
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  setIsCreating(false);
                  setFormName('');
                  setDescription('');
                }}
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                type="submit"
                disabled={creating}
              >
                {creating ? 'Creating...' : 'Create Webhook'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Webhooks list */}
      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '48px',
          fontSize: theme.typography.sizes.base,
          color: theme.colors.text.secondary
        }}>
          Loading webhooks...
        </div>
      ) : webhooks.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '48px',
          backgroundColor: 'white',
          border: `2px dashed ${theme.colors.border}`,
          borderRadius: '12px'
        }}>
          <p style={{
            margin: '0 0 16px 0',
            fontSize: theme.typography.sizes.lg,
            fontWeight: theme.typography.weights.medium,
            color: theme.colors.text.primary
          }}>
            No webhooks yet
          </p>
          <p style={{
            margin: '0 0 24px 0',
            fontSize: theme.typography.sizes.base,
            color: theme.colors.text.secondary
          }}>
            Create your first webhook to start integrating Typeform with Revenue Social
          </p>
          <Button
            variant="primary"
            size="md"
            onClick={() => setIsCreating(true)}
          >
            + Create Your First Webhook
          </Button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: '16px'
        }}>
          {webhooks.map((webhook) => (
            <div
              key={webhook.id}
              style={{
                backgroundColor: 'white',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '12px',
                padding: '24px'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '16px'
              }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    margin: '0 0 4px 0',
                    fontSize: theme.typography.sizes.lg,
                    fontWeight: theme.typography.weights.semibold,
                    color: theme.colors.text.primary
                  }}>
                    {webhook.formName}
                  </h3>
                  <p style={{
                    margin: '0 0 8px 0',
                    fontSize: theme.typography.sizes.sm,
                    color: theme.colors.text.secondary
                  }}>
                    ID: {webhook.webhookId}
                  </p>
                  {webhook.description && (
                    <p style={{
                      margin: '0',
                      fontSize: theme.typography.sizes.sm,
                      color: theme.colors.text.secondary
                    }}>
                      {webhook.description}
                    </p>
                  )}
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleDeleteWebhook(webhook.webhookId)}
                >
                  Delete
                </Button>
              </div>

              <div style={{
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                padding: '12px'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: theme.typography.sizes.xs,
                  fontWeight: theme.typography.weights.medium,
                  color: theme.colors.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Webhook URL
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <code style={{
                    flex: 1,
                    fontSize: '13px',
                    color: theme.colors.text.primary,
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                    backgroundColor: 'white',
                    padding: '8px',
                    borderRadius: '4px',
                    border: `1px solid ${theme.colors.border}`
                  }}>
                    {webhook.webhookUrl}
                  </code>
                  <Button
                    variant={copiedId === webhook.webhookId ? 'secondary' : 'primary'}
                    size="sm"
                    onClick={() => copyWebhookUrl(webhook.webhookUrl, webhook.webhookId)}
                  >
                    {copiedId === webhook.webhookId ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              <p style={{
                margin: '12px 0 0 0',
                fontSize: theme.typography.sizes.xs,
                color: theme.colors.text.light
              }}>
                Created {new Date(webhook.createdAt).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WebhookManager;
